import _ from "lodash";
import { PartnerReference, PartnerReferenceLike, RuntimePartnerReference } from "../types/PartnerReference";

export type PartnerRegistry = {
  partners: RuntimePartnerReference[];
  activePartners: RuntimePartnerReference[];
  partnersBySlug: Record<string, RuntimePartnerReference>;
  partnersByIri: Record<string, RuntimePartnerReference>;
  partnersByToken: Record<string, RuntimePartnerReference>;
};

const EMPTY_PARTNER_REGISTRY: PartnerRegistry = {
  partners: [],
  activePartners: [],
  partnersBySlug: {},
  partnersByIri: {},
  partnersByToken: {},
};

let partnerRegistry: PartnerRegistry = EMPTY_PARTNER_REGISTRY;

const getPartnerDisplayLabel = (partner: PartnerReferenceLike) => {
  if (!partner || typeof partner === "string") {
    return null;
  }

  return partner.brandName || partner.name || null;
};

const isLikelyIri = (value: string) => {
  return value.startsWith("/") || value.startsWith("http://") || value.startsWith("https://");
};

const getPartnerReferencePath = (value: string) => {
  if (!isLikelyIri(value)) {
    return null;
  }

  if (value.startsWith("/")) {
    return value;
  }

  try {
    return new URL(value).pathname;
  } catch {
    return null;
  }
};

const getPartnerReferenceId = (value: string) => {
  const path = getPartnerReferencePath(value);
  const match = path?.match(/\/(\d+)$/);

  return match?.[1] || null;
};

const getGenericPartnerReference = (value: string) => {
  const partnerId = getPartnerReferenceId(value);

  return partnerId ? `/partners/${partnerId}` : null;
};

const sortPartners = <T extends PartnerReference>(partners: T[]) => {
  return _.sortBy(partners, (partner) => (getPartnerDisplayLabel(partner) || "").toLowerCase());
};

const getRegisteredPartnerByToken = (value: string, registry: PartnerRegistry = partnerRegistry) => {
  return registry.partnersByToken[value] || null;
};

const getRegisteredPartner = (
  partner: PartnerReferenceLike,
  registry: PartnerRegistry = partnerRegistry
): RuntimePartnerReference | null => {
  if (!partner) {
    return null;
  }

  if (typeof partner !== "string") {
    const partnerIri = getPartnerIri(partner);
    if (partnerIri && registry.partnersByIri[partnerIri]) {
      return registry.partnersByIri[partnerIri];
    }

    if (partner.name) {
      return registry.partnersBySlug[partner.name] || null;
    }

    if (partner.brandName) {
      return getRegisteredPartnerByToken(partner.brandName, registry);
    }

    const partnerReferenceId = getPartnerReferenceId(partnerIri || "");
    return partnerReferenceId ? getRegisteredPartnerByToken(partnerReferenceId, registry) : null;
  }

  const partnerIri = getPartnerIri(partner);
  if (partnerIri && registry.partnersByIri[partnerIri]) {
    return registry.partnersByIri[partnerIri];
  }

  const partnerReferenceId = getPartnerReferenceId(partner);
  if (partnerReferenceId) {
    return getRegisteredPartnerByToken(partnerReferenceId, registry);
  }

  return getRegisteredPartnerByToken(partner, registry);
};

export const buildPartnerRegistry = ({
  insurerPartners,
  affiliatePartners,
}: {
  insurerPartners: RuntimePartnerReference[];
  affiliatePartners: RuntimePartnerReference[];
}): PartnerRegistry => {
  const partners = sortPartners([...insurerPartners, ...affiliatePartners]);
  const partnersBySlug: Record<string, RuntimePartnerReference> = {};
  const partnersByIri: Record<string, RuntimePartnerReference> = {};
  const partnersByToken: Record<string, RuntimePartnerReference> = {};

  _.forEach(partners, (partner) => {
    if (partner.name) {
      partnersBySlug[partner.name] = partner;
      partnersByToken[partner.name] = partner;
    }

    if (partner.brandName) {
      partnersByToken[partner.brandName] = partner;
    }

    const partnerIri = getPartnerIri(partner);
    if (partnerIri) {
      partnersByIri[partnerIri] = partner;

      const genericPartnerIri = getGenericPartnerReference(partnerIri);
      if (genericPartnerIri) {
        partnersByIri[genericPartnerIri] = partner;
      }

      const partnerReferenceId = getPartnerReferenceId(partnerIri);
      if (partnerReferenceId) {
        partnersByToken[partnerReferenceId] = partner;
      }
    }
  });

  return {
    partners,
    activePartners: partners.filter((partner) => partner.isActivePartnership === true),
    partnersBySlug,
    partnersByIri,
    partnersByToken,
  };
};

export const setPartnerRegistry = (registry: PartnerRegistry) => {
  partnerRegistry = registry;
};

export const getPartnerRegistry = () => partnerRegistry;

export const getPartnerIri = (partner: PartnerReferenceLike) => {
  if (!partner) {
    return null;
  }

  return typeof partner === "string" ? partner : partner["@id"] || partner.iri || null;
};

export const resolvePartnerSlug = (
  partner: PartnerReferenceLike,
  registry: PartnerRegistry = partnerRegistry
): string | null => {
  if (!partner) {
    return null;
  }

  if (typeof partner !== "string" && partner.name) {
    return partner.name;
  }

  const registeredPartner = getRegisteredPartner(partner, registry);
  if (registeredPartner?.name) {
    return registeredPartner.name;
  }

  if (typeof partner === "string") {
    if (isLikelyIri(partner)) {
      return null;
    }

    return getRegisteredPartnerByToken(partner, registry)?.name || partner;
  }

  if (partner.brandName) {
    return getRegisteredPartnerByToken(partner.brandName, registry)?.name || null;
  }

  return null;
};

export const isKnownPartner = (partner: PartnerReferenceLike, slug: string, registry?: PartnerRegistry) => {
  const resolvedSlug = resolvePartnerSlug(partner, registry);

  if (!resolvedSlug) {
    return false;
  }

  return resolvedSlug === slug;
};

export const getPartnerDisplayName = (
  partner: PartnerReferenceLike,
  registry: PartnerRegistry = partnerRegistry
) => {
  if (!partner) {
    return "";
  }

  const directLabel = getPartnerDisplayLabel(partner);
  if (directLabel) {
    return directLabel;
  }

  const registeredPartner = getRegisteredPartner(partner, registry);
  if (registeredPartner) {
    return getPartnerDisplayLabel(registeredPartner) || registeredPartner.name || "";
  }

  if (typeof partner === "string" && !isLikelyIri(partner)) {
    return _.startCase(partner);
  }

  const partnerIri = getPartnerIri(partner);
  return partnerIri ? _.startCase(_.last(partnerIri.split("/")) || partnerIri) : "";
};

export const matchesInsurancePartner = (
  partner: PartnerReferenceLike,
  insuranceShortName: string | null | undefined,
  registry: PartnerRegistry = partnerRegistry
) => {
  if (!insuranceShortName) {
    return false;
  }

  const resolvedPartner = typeof partner !== "string" ? partner : getRegisteredPartner(partner, registry);

  if (!resolvedPartner) {
    return false;
  }

  return [resolvedPartner.name, resolvedPartner.brandName]
    .filter((value): value is string => Boolean(value))
    .some((value) => value === insuranceShortName);
};
